// Preview (prototype preview): pick deciding values and see what each form
// would ask, drawn by the runtime's own form renderer. Tenant mode asks the
// server (`/preview` with the draft being edited — the same resolution the
// runtime gets); design mode decides with the shared rule algebra, because
// the server cannot read the designer's unsaved layer.

import { useEffect, useMemo, useState } from 'react'
import { KitModal } from '../../designerKit/KitModal'
import type { SqlModel } from '../../txn/types'
import type { UdaColumnDef, UdaResolution, UdaSetInfo } from '../types'
import { whyCaption } from '../merge'
import { applicableSets } from '../rules'
import { asColumnDef, driverLabel, formOf, tenantBodyOf, whenText } from './model'
import { useSetup } from './state'
import PreviewForm from './PreviewForm'

function FormPreview({ formNo, picks }: { formNo: string; picks: Record<string, string | null> }) {
  const { draft, mode, transport, objName, forms, project, labelOf } = useSetup()
  const form = formOf(draft.layer, formNo)
  const setup = forms.find(f => f.formNo === formNo)
  const [res, setRes] = useState<{ columns: UdaColumnDef[]; sets: UdaSetInfo[] } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const bodyKey = mode === 'tenant' ? JSON.stringify(tenantBodyOf(draft.layer, draft.design)) : ''
  const pickKey = JSON.stringify(picks)
  useEffect(() => {
    if (!form) return undefined
    let cancelled = false
    setError(null)
    if (mode === 'design') {
      const hit = applicableSets((form.sets || []).filter(s => !s.hidden), picks, form.drivers)
      const lab = labelOf(formNo)
      setRes({
        columns: hit.flatMap(s => (s.columns || []).filter(c => !c.retired && !c.hidden).map(c => ({
          ...asColumnDef(c, s.placement?.group || s.label || s.key), uda: { key: c.name, set_keys: [s.key] },
        }) as UdaColumnDef)),
        sets: hit.map(s => ({ key: s.key, label: s.label || s.key, group: s.placement?.group || s.label || s.key, why: whenText(form.drivers, s, lab) })),
      })
      return undefined
    }
    transport.preview(objName, formNo, picks, JSON.parse(bodyKey))
      .then((r: UdaResolution) => { if (!cancelled) setRes({ columns: r.columns || [], sets: r.sets || [] }) })
      .catch(err => {
        console.warn('[uda setup] preview failed', err)
        if (!cancelled) setError((err as Error)?.message || 'The preview could not be worked out')
      })
    return () => { cancelled = true }
  }, [formNo, pickKey, bodyKey, mode]) // eslint-disable-line react-hooks/exhaustive-deps
  if (!form) return null
  const model = { form_title: setup?.title || '', form_descr: '', form_no: formNo, main_table: '',
    columns: (res?.columns || []).map(c => ({ column: c })) } as unknown as SqlModel
  const captions: Record<string, string> = {}
  for (const s of res?.sets || []) {
    const g = String(s.group || s.label || '')
    if (g && s.why) captions[g] = whyCaption(s.why)
  }
  const visible = (res?.columns || []).filter(c => !c.uda?.driver)
  return (
    <>
      <div className="uds-sect">{setup?.title || `Form ${formNo}`}</div>
      {error ? <div className="uds-hint" style={{ color: 'var(--color-error)' }}>{error}</div>
        : !res ? <div className="uds-hint"><span className="uds-spin" /> Working it out…</div>
          : !visible.length ? <div className="uds-na">No additional attributes for this combination.</div>
            : (
              <div className="uds-rgrp" style={{ padding: '4px 14px 12px' }}>
                <PreviewForm model={model} values={{}} project={project} objName={objName} formNo={formNo} captions={captions} />
              </div>
            )}
    </>
  )
}

export default function PreviewDialog({ onClose }: { onClose: () => void }) {
  const { forms, draft, domains, labelOf } = useSetup()
  const shown = forms.filter(f => (formOf(draft.layer, f.formNo)?.drivers || []).length)
  useEffect(() => {
    for (const f of shown) domains.ensure(f.formNo, formOf(draft.layer, f.formNo)!.drivers)
  }, [shown.length]) // eslint-disable-line react-hooks/exhaustive-deps
  const [picks, setPicks] = useState<Record<string, Record<string, string | null>>>({})
  const pickOf = useMemo(() => (formNo: string, key: string): string | null => {
    const p = picks[formNo]?.[key]
    if (p !== undefined) return p
    const d = formOf(draft.layer, formNo)?.drivers.find(x => x.key === key)
    return d ? domains.domainOf(formNo, d).values[0]?.[0] ?? null : null
  }, [picks, draft.layer, domains])
  return (
    <KitModal open onClose={onClose} title="Preview" size="full">
      <div className="uds-dlg" style={{ display: 'grid', gap: 12 }}>
        {!shown.length && <div className="uds-na">No form is decided by anything yet.</div>}
        {shown.map(f => {
          const drivers = formOf(draft.layer, f.formNo)!.drivers
          const lab = labelOf(f.formNo)
          const current: Record<string, string | null> = {}
          for (const d of drivers) current[d.key] = pickOf(f.formNo, d.key)
          return (
            <div key={f.formNo} style={{ display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                {drivers.map(d => (
                  <label key={d.key} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13, fontWeight: 600 }}>
                    {driverLabel(d)}
                    <select className="uds-inp sm" style={{ width: 'auto' }} value={current[d.key] ?? ''}
                      onChange={e => { const v = e.target.value || null; setPicks(p => ({ ...p, [f.formNo]: { ...(p[f.formNo] || {}), [d.key]: v } })) }}>
                      <option value="">(not picked yet)</option>
                      {domains.domainOf(f.formNo, d).values.map(v => <option key={v[0]} value={v[0]}>{lab(d.key, v[0])}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              <FormPreview formNo={f.formNo} picks={current} />
            </div>
          )
        })}
      </div>
    </KitModal>
  )
}
